/**
 * 계약관리 (계약대장 · 원인행위 N:M · 조달채널 외부키)
 * - API: BdContract 전용 테이블 · 아니면 localStorage
 */

import { getCurrentFiscalYear } from 'components/forms';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { cacheSet } from 'services/commonQuery';
import { apiLoadContractBundle, apiSaveContractBundle } from 'contract/contractApi';

export const CONTRACT_MASTER_PREFIX = 'erp.contract.master';
export const CONTRACT_OBL_LINK_PREFIX = 'erp.contract.oblLink';
export const CONTRACT_EXT_REF_PREFIX = 'erp.contract.extRef';

/** 목적물 — 1차 물품·용역, 공사는 후속 */
export const CONTRACT_OBJECT = {
  GOODS: 'GOODS',
  SERVICE: 'SERVICE',
  CONSTRUCT: 'CONSTRUCT',
};

export const CONTRACT_OBJECT_OPTIONS = [
  { value: CONTRACT_OBJECT.GOODS, label: '물품' },
  { value: CONTRACT_OBJECT.SERVICE, label: '용역' },
  { value: CONTRACT_OBJECT.CONSTRUCT, label: '공사(후속)', disabled: true },
];

/** Phase A에서 등록 가능한 목적물 */
export const CONTRACT_OBJECT_PRIMARY = [CONTRACT_OBJECT.GOODS, CONTRACT_OBJECT.SERVICE];

export const CONTRACT_OBJECT_PRIMARY_OPTIONS = CONTRACT_OBJECT_OPTIONS.filter((o) =>
  CONTRACT_OBJECT_PRIMARY.includes(o.value)
);

export const CONTRACT_STATUS = {
  PLAN: 'PLAN',
  BID: 'BID',
  SIGNED: 'SIGNED',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  TERMINATED: 'TERMINATED',
  WARRANTY: 'WARRANTY',
};

export const CONTRACT_STATUS_OPTIONS = [
  { value: CONTRACT_STATUS.PLAN, label: '발주' },
  { value: CONTRACT_STATUS.BID, label: '낙찰중' },
  { value: CONTRACT_STATUS.SIGNED, label: '체결' },
  { value: CONTRACT_STATUS.ACTIVE, label: '이행' },
  { value: CONTRACT_STATUS.COMPLETED, label: '완료' },
  { value: CONTRACT_STATUS.TERMINATED, label: '해지' },
  { value: CONTRACT_STATUS.WARRANTY, label: '하자' },
];

/** 조달채널 — 주채널 없이 모두 병행 */
export const PROCUREMENT_CHANNEL = {
  G2B: 'G2B',
  SEOUL_CONTRACT: 'SEOUL_CONTRACT',
  LOCAL_CONTRACT: 'LOCAL_CONTRACT',
  INTERNAL: 'INTERNAL',
  OTHER: 'OTHER',
};

export const PROCUREMENT_CHANNEL_OPTIONS = [
  { value: PROCUREMENT_CHANNEL.G2B, label: '나라장터' },
  { value: PROCUREMENT_CHANNEL.SEOUL_CONTRACT, label: '서울계약마당' },
  { value: PROCUREMENT_CHANNEL.LOCAL_CONTRACT, label: '지방계약시스템' },
  { value: PROCUREMENT_CHANNEL.INTERNAL, label: '자체' },
  { value: PROCUREMENT_CHANNEL.OTHER, label: '기타' },
];

export const AWARD_METHOD = {
  ELIGIBILITY: 'ELIGIBILITY',
  NEGOTIATION: 'NEGOTIATION',
  LOWEST: 'LOWEST',
  PRIVATE_1: 'PRIVATE_1',
  PRIVATE_OPEN: 'PRIVATE_OPEN',
  OTHER: 'OTHER',
};

export const AWARD_METHOD_OPTIONS = [
  { value: AWARD_METHOD.ELIGIBILITY, label: '적격심사' },
  { value: AWARD_METHOD.NEGOTIATION, label: '협상' },
  { value: AWARD_METHOD.LOWEST, label: '최저가' },
  { value: AWARD_METHOD.PRIVATE_1, label: '1인견적수의' },
  { value: AWARD_METHOD.PRIVATE_OPEN, label: '전자공개수의' },
  { value: AWARD_METHOD.OTHER, label: '기타' },
];

function labelOf(options, code) {
  return options.find((o) => o.value === code)?.label || code || '';
}

export function contractObjectLabel(code) {
  return labelOf(CONTRACT_OBJECT_OPTIONS, code);
}

export function contractStatusLabel(code) {
  return labelOf(CONTRACT_STATUS_OPTIONS, code);
}

export function procurementChannelLabel(code) {
  return labelOf(PROCUREMENT_CHANNEL_OPTIONS, code);
}

export function awardMethodLabel(code) {
  return labelOf(AWARD_METHOD_OPTIONS, code);
}

function storageKey(prefix, tenantCode, fiscalYear) {
  return `${prefix}|${tenantCode || ''}|${fiscalYear || ''}`;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

const contractCache = new Map();

function contractCacheKey(tenantCode, fiscalYear) {
  return `${tenantCode || ''}|${fiscalYear || ''}`;
}

function keepRows(rows) {
  return (rows || []).filter((r) => r && r.mod !== 'D').map((r) => ({ ...r, mod: '' }));
}

export function todayYmd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function createContractUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `CTR-${crypto.randomUUID()}`;
  }
  return `CTR-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createLinkUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `COL-${crypto.randomUUID()}`;
  }
  return `COL-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createExtRefUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `CXR-${crypto.randomUUID()}`;
  }
  return `CXR-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyContract(partial = {}) {
  return {
    contractUid: '',
    fiscalYear: '',
    contractNo: '',
    contractName: '',
    objectType: CONTRACT_OBJECT.GOODS,
    status: CONTRACT_STATUS.SIGNED,
    awardMethod: AWARD_METHOD.OTHER,
    /** 대표 채널(표시용). 상세는 extRefs */
    primaryChannel: PROCUREMENT_CHANNEL.INTERNAL,
    contractDate: '',
    startDate: '',
    endDate: '',
    contractAmt: 0,
    vendorCode: '',
    vendorName: '',
    businessNo: '',
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    socialValueYn: 'N',
    remark: '',
    registerDate: '',
    mod: '',
    ...partial,
  };
}

/** 계약 ↔ 원인행위 N:M */
export function emptyOblLink(partial = {}) {
  return {
    linkUid: '',
    contractUid: '',
    obligationUid: '',
    obligationNo: '',
    linkedAmt: 0,
    remark: '',
    mod: '',
    ...partial,
  };
}

/** 조달채널별 외부키 (다건) */
export function emptyExtRef(partial = {}) {
  return {
    extRefUid: '',
    contractUid: '',
    channel: PROCUREMENT_CHANNEL.G2B,
    noticeNo: '',
    extContractNo: '',
    syncedAt: '',
    remark: '',
    mod: '',
    ...partial,
  };
}

export async function loadContracts(tenantCode, fiscalYear) {
  const bundle = await loadContractBundle(tenantCode, fiscalYear);
  return bundle.contracts || [];
}

export async function loadOblLinks(tenantCode, fiscalYear) {
  const bundle = await loadContractBundle(tenantCode, fiscalYear);
  return bundle.oblLinks || [];
}

export async function loadExtRefs(tenantCode, fiscalYear) {
  const bundle = await loadContractBundle(tenantCode, fiscalYear);
  return bundle.extRefs || [];
}

export async function saveContractBundle(tenantCode, fiscalYear, { contracts, oblLinks, extRefs }) {
  const fy = String(fiscalYear || '').trim();
  const nextC = contracts != null ? keepRows(contracts) : null;
  const nextL = oblLinks != null ? keepRows(oblLinks) : null;
  const nextE = extRefs != null ? keepRows(extRefs) : null;

  if (isBdApiEnabled()) {
    return apiSaveContractBundle(
      tenantCode,
      fy,
      { contracts: nextC, oblLinks: nextL, extRefs: nextE },
      () => createContractUid(),
      () => createLinkUid(),
      () => createExtRefUid()
    );
  }

  if (nextC != null) writeJson(storageKey(CONTRACT_MASTER_PREFIX, tenantCode, fy), nextC);
  if (nextL != null) writeJson(storageKey(CONTRACT_OBL_LINK_PREFIX, tenantCode, fy), nextL);
  if (nextE != null) writeJson(storageKey(CONTRACT_EXT_REF_PREFIX, tenantCode, fy), nextE);
  return loadContractBundle(tenantCode, fy);
}

export async function loadContractBundle(tenantCode, fiscalYear) {
  const fy = String(fiscalYear || getCurrentFiscalYear()).trim();
  if (isBdApiEnabled()) {
    return apiLoadContractBundle(tenantCode, fy);
  }
  const bundle = {
    contracts: readJson(storageKey(CONTRACT_MASTER_PREFIX, tenantCode, fy), []),
    oblLinks: readJson(storageKey(CONTRACT_OBL_LINK_PREFIX, tenantCode, fy), []),
    extRefs: readJson(storageKey(CONTRACT_EXT_REF_PREFIX, tenantCode, fy), []),
  };
  cacheSet(contractCache, contractCacheKey(tenantCode, fy), bundle);
  return bundle;
}

export function enrichContractRow(row) {
  const r = row || {};
  return {
    ...r,
    objectTypeNm: contractObjectLabel(r.objectType),
    statusNm: contractStatusLabel(r.status),
    primaryChannelNm: procurementChannelLabel(r.primaryChannel),
    awardMethodNm: awardMethodLabel(r.awardMethod),
  };
}

export function filterContracts(rows = [], filter = {}) {
  let list = (rows || []).filter((r) => r.mod !== 'D');
  const obj = String(filter.objectType || '').trim();
  if (obj) list = list.filter((r) => r.objectType === obj);
  const st = String(filter.status || '').trim();
  if (st) list = list.filter((r) => r.status === st);
  const ch = String(filter.primaryChannel || '').trim();
  if (ch) list = list.filter((r) => r.primaryChannel === ch);
  const kw = String(filter.keyword || '').trim().toLowerCase();
  if (kw) {
    list = list.filter((r) =>
      [r.contractNo, r.contractName, r.vendorName, r.remark]
        .map((x) => String(x || '').toLowerCase())
        .some((s) => s.includes(kw))
    );
  }
  // 1차: 물품·용역만 기본 노출 (공사 제외)
  if (filter.primaryOnly !== false) {
    list = list.filter((r) => CONTRACT_OBJECT_PRIMARY.includes(r.objectType));
  }
  return list;
}

export function linksOfContract(oblLinks = [], contractUid) {
  const uid = String(contractUid || '').trim();
  return (oblLinks || []).filter((l) => l.mod !== 'D' && l.contractUid === uid);
}

export function linksOfObligation(oblLinks = [], obligationUid) {
  const uid = String(obligationUid || '').trim();
  return (oblLinks || []).filter((l) => l.mod !== 'D' && l.obligationUid === uid);
}

export function extRefsOfContract(extRefs = [], contractUid) {
  const uid = String(contractUid || '').trim();
  return (extRefs || []).filter((e) => e.mod !== 'D' && e.contractUid === uid);
}

/**
 * 원인행위 계약 그리드 → 계약대장 생성 + N:M 링크
 * - 동일 contractNo가 있으면 재사용하고 링크만 추가
 */
export async function createContractsFromObligation({
  tenantCode,
  fiscalYear,
  obligation,
  contractRows = [],
}) {
  const fy = String(fiscalYear || '').trim();
  const oblUid = String(obligation?.obligationUid || '').trim();
  if (!oblUid) return { created: 0, linked: 0, bundle: await loadContractBundle(tenantCode, fy) };

  const rows = (contractRows || []).filter((r) => r && r.mod !== 'D');
  if (!rows.length) return { created: 0, linked: 0, bundle: await loadContractBundle(tenantCode, fy) };

  let { contracts, oblLinks, extRefs } = await loadContractBundle(tenantCode, fy);
  let created = 0;
  let linked = 0;

  const byNo = new Map(
    (contracts || [])
      .filter((c) => c.mod !== 'D' && String(c.contractNo || '').trim())
      .map((c) => [String(c.contractNo).trim(), c])
  );

  rows.forEach((row) => {
    const no = String(row.contractNo || '').trim();
    let contract = no ? byNo.get(no) : null;

    if (!contract) {
      const uid = row.contractUid && !byNo.has(no) ? row.contractUid : createContractUid();
      // objectType: 원인행위 유형 힌트
      let objectType = CONTRACT_OBJECT.GOODS;
      const ot = String(obligation?.obligationType || '').trim();
      if (ot === 'SERVICE') objectType = CONTRACT_OBJECT.SERVICE;
      else if (ot === 'GOODS') objectType = CONTRACT_OBJECT.GOODS;
      // CONSTRUCT 등: 1차 미지원 → 물품으로 생성 후 비고에 표시 가능

      contract = emptyContract({
        contractUid: uid,
        fiscalYear: fy,
        contractNo: no || `AUTO-${uid.slice(-8)}`,
        contractName: row.contractName || obligation?.remark || '원인행위 연동 계약',
        objectType,
        status: CONTRACT_STATUS.SIGNED,
        contractDate: row.contractDate || todayYmd(),
        contractAmt: Number(row.contractAmt) || Number(obligation?.obligationAmt) || 0,
        vendorName: row.vendorName || '',
        deptCode: obligation?.deptCode || '',
        deptName: obligation?.deptName || '',
        projectCode: obligation?.projectCode || '',
        projectName: obligation?.projectName || '',
        subjectCode: obligation?.subjectCode || '',
        subjectName: obligation?.subjectName || '',
        fundCode: obligation?.fundCode || '',
        fundName: obligation?.fundName || '',
        remark: row.remark || '',
        registerDate: todayYmd(),
        primaryChannel: PROCUREMENT_CHANNEL.INTERNAL,
        mod: 'A',
      });
      contracts = [...contracts, contract];
      if (contract.contractNo) byNo.set(String(contract.contractNo).trim(), contract);
      created += 1;
    }

    const already = (oblLinks || []).some(
      (l) =>
        l.mod !== 'D' &&
        l.contractUid === contract.contractUid &&
        l.obligationUid === oblUid
    );
    if (!already) {
      oblLinks = [
        ...oblLinks,
        emptyOblLink({
          linkUid: createLinkUid(),
          contractUid: contract.contractUid,
          obligationUid: oblUid,
          obligationNo: obligation?.obligationNo || '',
          linkedAmt: Number(row.contractAmt) || Number(contract.contractAmt) || 0,
          remark: '원인행위→계약대장생성',
          mod: 'A',
        }),
      ];
      linked += 1;
    }
  });

  const bundle = await saveContractBundle(tenantCode, fy, { contracts, oblLinks, extRefs });
  return { created, linked, bundle };
}

/**
 * 계약 선택 → 원인행위에 N:M 링크 (+ 원인행위 계약 그리드용 스냅샷 행)
 */
export async function linkContractsToObligation({
  tenantCode,
  fiscalYear,
  obligation,
  contractsToLink = [],
}) {
  const fy = String(fiscalYear || '').trim();
  const oblUid = String(obligation?.obligationUid || '').trim();
  if (!oblUid) return { linked: 0, snapshotRows: [], bundle: await loadContractBundle(tenantCode, fy) };

  let { contracts, oblLinks, extRefs } = await loadContractBundle(tenantCode, fy);
  let linked = 0;
  const snapshotRows = [];

  (contractsToLink || []).forEach((c) => {
    const cUid = String(c.contractUid || '').trim();
    if (!cUid) return;
    const master = (contracts || []).find((x) => x.contractUid === cUid && x.mod !== 'D') || c;
    const already = (oblLinks || []).some(
      (l) => l.mod !== 'D' && l.contractUid === cUid && l.obligationUid === oblUid
    );
    if (!already) {
      oblLinks = [
        ...oblLinks,
        emptyOblLink({
          linkUid: createLinkUid(),
          contractUid: cUid,
          obligationUid: oblUid,
          obligationNo: obligation?.obligationNo || '',
          linkedAmt: Number(master.contractAmt) || 0,
          remark: '계약불러오기',
          mod: 'A',
        }),
      ];
      linked += 1;
    }
    snapshotRows.push({
      contractUid: cUid,
      obligationUid: oblUid,
      contractNo: master.contractNo || '',
      contractName: master.contractName || '',
      contractDate: master.contractDate || '',
      contractAmt: Number(master.contractAmt) || 0,
      vendorName: master.vendorName || '',
      remark: master.remark || '',
      mod: 'A',
    });
  });

  const bundle = await saveContractBundle(tenantCode, fy, { contracts, oblLinks, extRefs });
  return { linked, snapshotRows, bundle };
}

export async function ensureContractSeed(tenantCode, fiscalYear) {
  const fy = String(fiscalYear || getCurrentFiscalYear()).trim();
  const existing = await loadContracts(tenantCode, fy);
  if (Array.isArray(existing) && existing.length) {
    return loadContractBundle(tenantCode, fy);
  }

  const c1 = emptyContract({
    contractUid: createContractUid(),
    fiscalYear: fy,
    contractNo: `${fy}-G-0001`,
    contractName: '사무용품 단가계약(시드)',
    objectType: CONTRACT_OBJECT.GOODS,
    status: CONTRACT_STATUS.ACTIVE,
    awardMethod: AWARD_METHOD.PRIVATE_OPEN,
    primaryChannel: PROCUREMENT_CHANNEL.G2B,
    contractDate: `${fy}-02-15`,
    startDate: `${fy}-02-15`,
    endDate: `${fy}-12-31`,
    contractAmt: 5000000,
    vendorName: '(주)오피스월드',
    businessNo: '123-81-67890',
    deptCode: 'A01',
    deptName: 'a팀',
    projectCode: 'P100',
    projectName: '기관운영',
    subjectCode: '51101',
    subjectName: '일반운영비',
    socialValueYn: 'Y',
    remark: '나라장터 전자공개수의 시드',
    registerDate: todayYmd(),
    mod: 'A',
  });
  const c2 = emptyContract({
    contractUid: createContractUid(),
    fiscalYear: fy,
    contractNo: `${fy}-S-0001`,
    contractName: '전산유지보수 용역(시드)',
    objectType: CONTRACT_OBJECT.SERVICE,
    status: CONTRACT_STATUS.SIGNED,
    awardMethod: AWARD_METHOD.NEGOTIATION,
    primaryChannel: PROCUREMENT_CHANNEL.SEOUL_CONTRACT,
    contractDate: `${fy}-03-01`,
    startDate: `${fy}-03-01`,
    endDate: `${fy}-12-31`,
    contractAmt: 12000000,
    vendorName: '한국전산시스템',
    businessNo: '214-85-12345',
    deptCode: 'A01',
    deptName: 'a팀',
    projectCode: 'P100',
    projectName: '기관운영',
    subjectCode: '51101',
    subjectName: '일반운영비',
    remark: '서울계약마당 시드',
    registerDate: todayYmd(),
    mod: 'A',
  });

  const extRefs = [
    emptyExtRef({
      extRefUid: createExtRefUid(),
      contractUid: c1.contractUid,
      channel: PROCUREMENT_CHANNEL.G2B,
      noticeNo: 'R25BK001234',
      extContractNo: '2026-G2B-001',
      syncedAt: todayYmd(),
      mod: 'A',
    }),
    emptyExtRef({
      extRefUid: createExtRefUid(),
      contractUid: c2.contractUid,
      channel: PROCUREMENT_CHANNEL.SEOUL_CONTRACT,
      noticeNo: 'SE-2026-88',
      extContractNo: 'CTR-SEOUL-2026-88',
      syncedAt: todayYmd(),
      mod: 'A',
    }),
  ];

  return saveContractBundle(tenantCode, fy, {
    contracts: [c1, c2],
    oblLinks: [],
    extRefs,
  });
}
